import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Newspaper, Calendar, Tag } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import axios from "axios";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterPage() {
  const [newsletters, setNewsletters] = useState([]);
  const [filter, setFilter] = useState("Alle");

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/newsletter-archive`).then(r => setNewsletters(r.data || [])).catch(() => {});
  }, []);

  usePageSeo({
    title: "Newsletter-Archiv | LAB336",
    description: "Lesen Sie vergangene Newsletter-Ausgaben von LAB336 zu KI, Digitalisierung und Prozessoptimierung für Schweizer KMU.",
    canonical: `${BASE_URL}/newsletter`,
  });

  const categories = ["Alle", ...new Set(newsletters.map(n => n.category).filter(Boolean))];
  const filtered = filter === "Alle" ? newsletters : newsletters.filter(n => n.category === filter);

  return (
    <div className="min-h-screen bg-white" data-testid="newsletter-page">
      <Navigation />
      <header className="bg-lab-bg-dark pt-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 flex items-center justify-center">
              <Newspaper className="w-7 h-7 text-lab-accent" />
            </div>
            <div>
              <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white">Newsletter-Archiv</h1>
              <p className="text-gray-400 mt-1">Alle vergangenen Ausgaben zum Nachlesen</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
        {/* Newsletter Anmeldung CTA */}
        <div className="bg-lab-bg-light border border-lab-border p-8 mb-12" data-testid="newsletter-subscribe-cta">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1">
              <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-2">Keine Ausgabe verpassen</h2>
              <p className="text-lab-text-muted text-sm">Erhalten Sie monatlich praxisnahe Insights zu KI, Digitalisierung und Prozessoptimierung direkt in Ihr Postfach.</p>
            </div>
            <Link to="/#contact" className="bg-lab-primary text-white px-6 py-3 text-sm font-medium hover:bg-lab-accent hover:text-lab-primary transition-colors whitespace-nowrap" data-testid="newsletter-subscribe-btn">
              Newsletter abonnieren
            </Link>
          </div>
        </div>

        {/* Filter */}
        {categories.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map(cat => (
              <button key={cat} onClick={() => setFilter(cat)} className={`text-sm px-4 py-2 border transition-colors ${filter === cat ? "bg-lab-primary text-white border-lab-primary" : "bg-white text-lab-text-muted border-lab-border hover:border-lab-accent"}`} data-testid={`newsletter-filter-${cat}`}>
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* List View */}
        {filtered.length === 0 ? (
          <p className="text-lab-text-muted text-center py-16">Noch keine Newsletter veröffentlicht.</p>
        ) : (
          <div className="space-y-4">
            {filtered.map((nl, i) => (
              <motion.div key={nl.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={`/newsletter/${nl.id}`} className="block w-full text-left p-6 border border-lab-border bg-white hover:border-lab-accent transition-colors group" data-testid={`newsletter-card-${nl.id}`}>
                  <div className="flex items-center gap-3 text-xs text-lab-text-muted mb-2">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(nl.published_at).toLocaleDateString("de-CH", { day: "numeric", month: "long", year: "numeric" })}</span>
                    <span className="bg-lab-bg-light px-2 py-0.5 border border-lab-border">{nl.category}</span>
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-lab-primary group-hover:text-lab-accent transition-colors">{nl.title}</h3>
                  {nl.summary && <p className="text-lab-text-muted text-sm mt-2 line-clamp-2">{nl.summary}</p>}
                  <span className="text-lab-accent text-sm mt-3 inline-block">Weiterlesen →</span>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <footer className="bg-lab-bg-dark border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} LAB 336. Alle Rechte vorbehalten.</p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-sm transition-colors">Startseite</Link>
        </div>
      </footer>
    </div>
  );
}
